// @story HIR-TC4
/* One criterion's mark, on either kind of scorecard (1 Nov 2026):
 *   marks   a number from 0 to the criterion's most marks (the demo rubric,
 *           or a stage's own scorecard);
 *   levels  a level from 1 to 4, halves allowed, chosen from the group's
 *           level wording (the interview rubrics). The panellist never sees
 *           a weight to multiply: the server weights the levels.
 * markTotal() is the same arithmetic as the server's, for the running total. */
import React from "react";
import { Input, Select } from "../ds";

const STEPS = [1, 1.5, 2, 2.5, 3, 3.5, 4];

export function isLevels(row) {
  return row && row.scale === "levels";
}

/* Earned, most and percentage for a card, the way the server works them out. */
export function markTotal(rows) {
  if (rows.length && rows.every(isLevels)) {
    const given = rows.filter((r) => r.score !== "" && r.score !== null && r.score !== undefined);
    const weights = rows.reduce((a, r) => a + (Number(r.weight) || 0), 0);
    const earned = given.reduce((a, r) => a + (Number(r.weight) || 0) * (Number(r.score) / 4), 0);
    return { levels: true, complete: given.length === rows.length, pct: weights > 0 ? Math.round((earned / weights) * 100) : 0 };
  }
  const earned = rows.reduce((a, c) => a + Math.min(Number(c.score) || 0, Number(c.maxMarks) || 0), 0);
  const max = rows.reduce((a, c) => a + (Number(c.maxMarks) || 0), 0);
  return { levels: false, complete: true, earned, max, pct: max > 0 ? Math.round((earned / max) * 100) : 0 };
}

/* A blank row for a criterion from the server: levels start unchosen. */
export function blankRow(c, was) {
  if (isLevels(c)) {
    return { label: c.label, scale: "levels", weight: Number(c.weight) || 0, maxMarks: 4, levels: c.levels || [], score: was ? Number(was.score) : "" };
  }
  return { label: c.label, maxMarks: Number(c.maxMarks) || 0, score: was ? Number(was.score) || 0 : 0 };
}

function levelName(levels, step) {
  const whole = (levels || []).find((l) => l.level === step);
  if (whole) return `${step} — ${whole.name}`;
  return `${step} — between ${Math.floor(step)} and ${Math.ceil(step)}`;
}

export default function RubricMark({ id, row, disabled, onChange }) {
  if (isLevels(row)) {
    const chosen = (row.levels || []).find((l) => l.level === Math.floor(Number(row.score)));
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 4, width: 260 }}>
        <Select
          value={row.score === "" ? "" : String(row.score)}
          disabled={disabled}
          placeholder="Choose a level…"
          onChange={(v) => onChange(v === "" ? "" : Number(v))}
          options={STEPS.map((s) => ({ value: String(s), label: levelName(row.levels, s) }))}
        />
        {chosen && <span style={{ fontSize: "var(--fs-caption)", color: "var(--text-muted)" }}>{chosen.text}</span>}
      </div>
    );
  }
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <span style={{ width: 90 }}>
        <Input
          id={id}
          type="number"
          value={row.score}
          disabled={disabled}
          onChange={(e) => onChange(Math.max(0, Math.min(Number(row.maxMarks) || 0, Number(e.target.value) || 0)))}
        />
      </span>
      <span style={{ fontSize: "var(--fs-body-sm)", color: "var(--text-muted)", width: 40 }}>/ {row.maxMarks}</span>
    </span>
  );
}
